import { useQuery } from 'react-query';
import toastr from 'react-hot-toast';
import React from 'react';
import { fetchWrapper } from 'utils/fetchWrapper';
import { SPARK_RESOURCE_TYPES } from 'constants/common';
import { alphabeticalSort } from 'utilities/sort';
import { generateURL } from 'utilities/hooks/urlGeneration';

type Tag = {
  id: string;
  attributes: {
    name: string;
    description: string;
    iconURL: string;
    key: string;
  };
};

type TagResponse = {
  data: Tag[];
};

const getTags = () => {
  const url = generateURL(SPARK_RESOURCE_TYPES.TAGS, []);
  return fetchWrapper<TagResponse>({
    url: url,
    method: 'GET',
  });
};

export function useGetTags() {
  const queryResult = useQuery(['tags'], getTags, {
    onError: (error) => {
      if (error instanceof Error) {
        toastr.error(`Failed to get Tags: ${error.message}`);
      }
    },
  });

  const { data } = queryResult;
  const { data: tags } = data || {};

  const sortedTags = tags?.sort((a, b) =>
    alphabeticalSort(a.attributes.name, b.attributes.name)
  );

  const filterTags = React.useMemo(() => {
    return sortedTags?.filter((tag) => tag.attributes.name);
  }, [sortedTags]);

  const idMap = sortedTags?.reduce<Record<string, Tag>>((acc, curr) => {
    return { ...acc, [curr.id]: curr };
  }, {});

  return {
    tags: filterTags,
    idMap,
    allTags: sortedTags,
    ...queryResult,
  };
}
